import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { orgPlugin } from '@backstage/plugin-org';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';

import { OcpAppliancePagePage } from '@internal/plugin-ocp-appliance-page';
import { CcpaPage } from '@electrolux/plugin-ccpa';
import { ConfigFilesPage } from '@internal/plugin-config-files';
import { UserPage } from '@internal/plugin-user-page';
import { CnsPage } from '@internal/plugin-cns';

import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { SignInPage, SignInProviderConfig } from '@backstage/core-components';

import type { IdentityApi } from '@backstage/core-plugin-api';
import { IdpMainPage, TasksList } from '@internal/plugin-catalog-module-idp';
import { VpnPage, VpnAdminPage } from '@internal/plugin-vpn';
import { ServiceCatalogPage } from '@internal/plugin-service-catalog';
import { AuditsPage } from '@internal/plugin-audits';
import { SlackPage } from '@internal/plugin-slack';
import { LabmatePage } from '@internal/plugin-labmate';
import { IdpCreatePage, IdpPage } from '@internal/plugin-catalog-module-idp';
import { SearchPage } from '@backstage/plugin-search';
import { OcpApplianceRegistrationPage } from '@internal/backstage-plugin-ocp-appliance-registration';
import { ApplianceDebuggerPage } from '@internal/backstage-plugin-appliance-debugger';
import { DamCapabilityPage } from '@internal/plugin-dam-capability';
import { HaclMapperMsgConversionPage } from '@internal/backstage-plugin-hacl-mapper-message-conversion';
import { SecDashPage } from '@internal/plugin-secdash';
import { FileStorePluginPage } from '@internal/backstage-plugin-file-store-plugin';
import { ReportPage } from '@internal/plugin-test-report';
import { ApiExporterPage } from '@internal/plugin-api-exporter';

const microsoftAuthProvider: SignInProviderConfig = {
  id: 'azure-auth-provider',
  title: 'Microsoft Active Directory',
  message: 'Sign in to Phoenix using your Active Directory account.',
  apiRef: microsoftAuthApiRef,
};

const app = createApp({
  apis,
  components: {
    SignInPage: props => {
      return (
        <SignInPage
          {...props}
          auto
          provider={microsoftAuthProvider}
          onSignInSuccess={async (identityApi: IdentityApi) => {
            props.onSignInSuccess(identityApi);
          }}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog-page" />} />
    <Route path="/config-files" element={<ConfigFilesPage />} />
    <Route path="/user-page/:env/:region/:username" element={<UserPage />} />
    <Route path="/user-page" element={<UserPage />} />
    <Route path="/catalog-page" element={<ServiceCatalogPage />} />
    <Route
      path="/ocp-appliance-page/:env/:region/:applianceId"
      element={<OcpAppliancePagePage />}
    />
    <Route path="/ocp-appliance-page" element={<OcpAppliancePagePage />} />
    <Route path="/ccpa" element={<CcpaPage />} />
    <Route path="/signal" element={<CnsPage />} />

    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {/* {searchPage} */}
    </Route>
    <Route path="/catalog/idp" element={<IdpPage />} />
    <Route path="/catalog/idp/create" element={<IdpCreatePage />} />
    <Route path="/catalog/idp/:resource/:id" element={<IdpMainPage />} />
    <Route path="/catalog/tasks" element={<TasksList />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/vpn" element={<VpnPage />} />
    <Route path="/vpn/admin" element={<VpnAdminPage />} />
    <Route path="/vpn/:user" element={<VpnPage />} />
    <Route path="/audits" element={<AuditsPage />} />
    <Route path="/slack" element={<SlackPage />} />
    <Route path="/labmate" element={<LabmatePage />} />
    <Route path="/test-report" element={<ReportPage />} />
    <Route path="/api-exporter" element={<ApiExporterPage />} />
    <Route
      path="/ocp-appliance-registration"
      element={<OcpApplianceRegistrationPage />}
    />
    <Route path="/appliance-debugger" element={<ApplianceDebuggerPage />} />
    <Route path="/dam-capability" element={<DamCapabilityPage />} />
    <Route
      path="/hacl-mapper-message-conversion"
      element={<HaclMapperMsgConversionPage />}
    />
    <Route path="/secdash" element={<SecDashPage />} />
    <Route path="/file-store-plugin" element={<FileStorePluginPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2500} />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
